import { memo, useMemo } from 'react';
import { Link } from 'react-router';
import {
  Button,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { getRetailListItemButtonStyles, getRetailListItemIconStyles } from './styles';
import { SystemStyleObject } from '@mui/system';
import { NavRoute, getListItemStyles, getListItemTextStyles } from '@shared/components';
import './navigation.css';

interface Props extends NavRoute {
  expandedView: boolean;
  overridingListItemStyles?: SystemStyleObject<Theme>;
  onClick?: () => void;
}

function NavItemBase({
  to,
  isActive,
  title,
  Icon,
  expandedView,
  overridingListItemStyles,
  onClick,
}: Props) {
  const listItemStyles = useMemo(
    () => getListItemStyles(expandedView, overridingListItemStyles),
    [expandedView, overridingListItemStyles]
  );
  const listItemButtonStyles = useMemo(() => getRetailListItemButtonStyles(isActive), [isActive]);
  const listItemIconStyles = useMemo(() => getRetailListItemIconStyles(isActive), [isActive]);
  const listItemTextStyles = useMemo(() => getListItemTextStyles(expandedView), [expandedView]);

  const retailButtonWidth = expandedView ? undefined : '48px';

  return (
    <ListItem disablePadding sx={listItemStyles}>
      <Tooltip title={expandedView ? '' : title} placement="right" arrow>
        {isActive ? (
          <Button
            className="active-nav-button"
            sx={{
              width: retailButtonWidth,
              maxWidth: retailButtonWidth,
              minWidth: retailButtonWidth,

              '&.MuiButton-root:hover': {
                border: 'none',
                background: 'none',
                boxShadow: 'none',
              },
            }}
          >
            <span className="active-nav-ellipse-lightest-overlay" />
            <span className="active-nav-ellipse-darkest-overlay" />
            <Link
              to={to}
              className="active-button-link"
              style={{
                padding: expandedView ? undefined : 0,
                justifyContent: expandedView ? undefined : 'center',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={listItemIconStyles}>
                  <Icon fontSize="small" sx={{ color: 'nav.text' }} />
                </ListItemIcon>

                <ListItemText
                  disableTypography
                  primary={
                    <Typography sx={{ fontWeight: isActive ? 600 : 500 }}>{title}</Typography>
                  }
                  sx={listItemTextStyles}
                />
              </span>
            </Link>
          </Button>
        ) : (
          <ListItemButton
            to={to}
            component={Link}
            onClick={onClick}
            selected={isActive}
            sx={listItemButtonStyles}
          >
            <ListItemIcon sx={listItemIconStyles}>
              <Icon fontSize="small" />
            </ListItemIcon>

            <ListItemText
              disableTypography
              primary={<Typography sx={{ fontWeight: isActive ? 600 : 500 }}>{title}</Typography>}
              sx={listItemTextStyles}
            />
          </ListItemButton>
        )}
      </Tooltip>
    </ListItem>
  );
}

export const RetailNavItem = memo(NavItemBase);
